import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TinyColor } from '@ctrl/tinycolor';
import { Subscription } from 'rxjs';
import { SubService } from 'src/app/services/sub.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { ModoExibicaoPersistidoService } from 'src/app/services/modo-exibicao-persistido.service';
import { ColorFormat } from 'src/app/components/color-picker/interfaces';

interface BeConfiguracoes {
  id?: number;
  nrCelularBot: string;
  dsCorTeste: string;
  dsCorMesAnterior: string;
  dsCorMesAtual: string;
  dsCorMesProximo: string;
}

@Component({
  selector: 'app-configuracoes',
  templateUrl: './configuracoes.component.html',
  styleUrls: ['./configuracoes.component.css']
})
export class ConfiguracoesComponent implements OnInit, OnDestroy {
  public subscription?: Subscription;
  public configFormGroup: FormGroup;
  public configuracao?: BeConfiguracoes;
  public secao: 'campos' | 'padroes' | 'formasPagamento' | 'bancos' | 'regras' = 'campos';
  public loading = false;
  public activeColorField = '';
  public activeColorLabel = '';
  public activeColorValue = '#FFFFFFFF';
  public activeColorFormat: ColorFormat = 'hex';

  private readonly chaveSecao = 'configuracoes.secao';

  private nrCelularBotFormControl = new FormControl<string>("", [
    Validators.required,
    Validators.pattern(/^\d{10,13}$/)
  ]);
  private dsCorTesteFormControl = new FormControl<string>("FF0000FF", [Validators.required]);
  private dsCorMesAnteriorFormControl = new FormControl<string>("3f51b5FF", [Validators.required]);
  private dsCorMesAtualFormControl = new FormControl<string>("34A853FF", [Validators.required]);
  private dsCorMesProximoFormControl = new FormControl<string>("3f51b5FF", [Validators.required]);

  public constructor(
    private httpClient: HttpClient,
    private sub: SubService,
    private snackBarService: SnackBarService,
    private modoExibicaoPersistidoService: ModoExibicaoPersistidoService
  ) {
    this.configFormGroup = new FormGroup({
      nrCelularBot: this.nrCelularBotFormControl,
      dsCorTeste: this.dsCorTesteFormControl,
      dsCorMesAnterior: this.dsCorMesAnteriorFormControl,
      dsCorMesAtual: this.dsCorMesAtualFormControl,
      dsCorMesProximo: this.dsCorMesProximoFormControl,
    });
  }

  public ngOnInit(): void {
    if (this.modoExibicaoPersistidoService.deveRestaurarModoExibicao()) {
      const secao = sessionStorage.getItem(this.chaveSecao);
      if (secao === 'campos' || secao === 'padroes' || secao === 'formasPagamento' || secao === 'bancos' || secao === 'regras')
        this.secao = secao;
    }
    else
      sessionStorage.setItem(this.chaveSecao, 'campos');
    this.sub.clear();
    this.carregarConfiguracoes();
  }

  public exibirSecao(secao: 'campos' | 'padroes' | 'formasPagamento' | 'bancos' | 'regras'): void {
    this.secao = secao;
    sessionStorage.setItem(this.chaveSecao, secao);
  }

  public ngOnDestroy(): void {
    this.sub.clear();
    this.unsubscribe();
  }

  private carregarConfiguracoes(): void {
    this.loading = true;
    
    this.httpClient.get<BeConfiguracoes>(
      `/api/Configuracoes`
    ).subscribe({
      next: (res) => {
        this.configuracao = res;
        this.preencherFormulario(res);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.snackBarService.exibirErro(`Erro ao carregar configurações! - ${err.status} - ${err.statusText}`);
        console.error('Erro ao carregar configurações:', err);
      }
    });
  }

  private preencherFormulario(config: BeConfiguracoes): void {
    this.configFormGroup.patchValue({
      nrCelularBot: config.nrCelularBot,
      dsCorTeste: config.dsCorTeste,
      dsCorMesAnterior: config.dsCorMesAnterior,
      dsCorMesAtual: config.dsCorMesAtual,
      dsCorMesProximo: config.dsCorMesProximo,
    });
  }

  public abrirSeletorCor(campo: string, label: string): void {
    const valoresAtuais: { [key: string]: string } = this.configFormGroup.value;
    const corAtual = valoresAtuais[campo] || '';

    this.activeColorField = campo;
    this.activeColorLabel = label;
    this.activeColorValue = this.toPickerValue(corAtual);
    this.activeColorFormat = 'hex';
  }

  public onActiveColorChange(value: string): void {
    if (!this.activeColorField) {
      return;
    }

    this.configFormGroup.patchValue({
      [this.activeColorField]: this.toHexValue(value)
    });
  }

  public obterCorTexto(campo: string): string {
    const valoresAtuais: { [key: string]: string } = this.configFormGroup.value;
    const hex = valoresAtuais[campo] || '';

    if (!hex) {
      return '';
    }

    if (this.activeColorField === campo) {
      return this.formatForDisplay(this.activeColorValue, this.activeColorFormat);
    }

    return hex.toUpperCase();
  }

  public obterCorPreview(campo: string): string {
    const valoresAtuais: { [key: string]: string } = this.configFormGroup.value;
    const hex = valoresAtuais[campo];
    if (!hex || hex.length < 8) return "#000000";
    
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const a = parseInt(hex.substring(6, 8), 16) / 255;
    
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }

  private toPickerValue(hex: string): string {
    if (!hex) {
      return '#FFFFFFFF';
    }

    const clean = hex.replace('#', '').trim();
    if (clean.length === 6) {
      return `#${clean}FF`;
    }

    if (clean.length === 8) {
      return `#${clean}`;
    }

    const fallback = new TinyColor(hex);
    return fallback.isValid ? fallback.toHex8String() : '#FFFFFFFF';
  }

  private toHexValue(value: string): string {
    const color = new TinyColor(value);
    if (!color.isValid) {
      return 'FFFFFFFF';
    }

    return color.toHex8().toUpperCase();
  }

  private formatForDisplay(value: string, format: ColorFormat): string {
    const color = new TinyColor(value);
    if (!color.isValid) {
      return '';
    }

    if (format === 'rgb') {
      return color.toRgbString();
    }

    if (format === 'hsl') {
      return color.toHslString();
    }

    if (format === 'hsv') {
      return color.toHsvString();
    }

    return color.toHex8().toUpperCase();
  }

  public onSalvar(): void {
    if (this.configFormGroup.invalid) {
      this.snackBarService.exibirErro('Preencha todos os campos corretamente!');
      return;
    }

    this.unsubscribe();
    this.subscription = this.httpClient.post<BeConfiguracoes>(
      `/api/Configuracoes`, this.configFormGroup.value
    ).subscribe({
      next: () => {
        this.unsubscribe();
        this.snackBarService.exibirSucesso('Configurações salvas com sucesso!');
        this.carregarConfiguracoes();
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro ao salvar configurações! - ${err.status} - ${err.statusText}`);
      }
    });
  }

  private unsubscribe(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }
}
